// frontend/src/components/shared/NotificationBell.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Clock, Bike, Package, MessageSquare, MapPin, DollarSign, User, Headphones, Award } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications'; // Updated import
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/badge';
import toast from 'react-hot-toast';
import type { Notification } from '../../types/notification.types';

// ... rest of the file remains the same

// Helper function to safely get order ID string
const getOrderIdString = (orderId: any): string | null => {
  if (!orderId) return null;
  if (typeof orderId === 'string') return orderId;
  if (typeof orderId === 'object') {
    // If it's an object with _id
    if (orderId._id && typeof orderId._id === 'string') return orderId._id;
    if (orderId._id && orderId._id.toString) return orderId._id.toString();
    // If it has toString method
    if (orderId.toString && typeof orderId.toString === 'function') {
      const str = orderId.toString();
      if (str !== '[object Object]') return str;
    }
  }
  return null;
};

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications, playSound, fetchNotifications, loading } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order_accepted':
        return <Check className="h-4 w-4 text-emerald-600" />;
      case 'offer_accepted':
        return <Award className="h-4 w-4 text-purple-600" />;
      case 'payment_received':
        return <DollarSign className="h-4 w-4 text-blue-600" />;
      case 'service_started':
        return <Bike className="h-4 w-4 text-indigo-600" />;
      case 'destination_reached':
        return <MapPin className="h-4 w-4 text-orange-600" />;
      case 'order_completed':
        return <CheckCheck className="h-4 w-4 text-green-600" />;
      case 'new_message':
        return <MessageSquare className="h-4 w-4 text-blue-600" />;
      case 'system':
        return <Headphones className="h-4 w-4 text-gray-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getNotificationTitle = (type: string): string => {
    switch (type) {
      case 'order_accepted':
        return '✅ Order Accepted';
      case 'offer_accepted':
        return '🎯 Offer Accepted';
      case 'payment_received':
        return '💰 Payment Received';
      case 'service_started':
        return '🚴 Service Started';
      case 'destination_reached':
        return '🏁 Destination Reached';
      case 'order_completed':
        return '🎉 Order Completed';
      case 'new_message':
        return '💬 New Message';
      case 'system':
        return '🔔 System Notification';
      default:
        return 'Notification';
    }
  };

  const getNotificationBgColor = (type: string, isRead: boolean) => {
    if (isRead) return 'bg-gray-50';
    switch (type) {
      case 'order_accepted':
      case 'offer_accepted':
        return 'bg-emerald-50';
      case 'payment_received':
        return 'bg-blue-50';
      case 'service_started':
        return 'bg-purple-50';
      case 'destination_reached':
        return 'bg-orange-50';
      case 'order_completed':
        return 'bg-green-50';
      case 'new_message':
        return 'bg-indigo-50';
      default:
        return 'bg-gray-50';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification._id);
    
    // Safely get order ID string
    const orderIdString = getOrderIdString(notification.orderId);
    
    if (orderIdString) {
      navigate(`/order/${orderIdString}`);
    }
    
    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return 'Just now';
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Just now';
      }
      
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
      if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
      return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Just now';
    }
  };

  // Safely get display order ID
  const getDisplayOrderId = (notification: Notification): string => {
    const orderIdString = getOrderIdString(notification.orderId);
    if (orderIdString) {
      return orderIdString.slice(-8);
    }
    return '';
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full border-2 border-white text-xs text-white flex items-center justify-center font-bold"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[100]"
            style={{ 
              maxWidth: 'calc(100vw - 2rem)',
              right: 0,
              top: '100%'
            }}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-green-50">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 hover:bg-emerald-100 rounded-lg transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-96 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No notifications</p>
                  <p className="text-xs text-gray-400 mt-1">We'll notify you when something happens</p>
                </div>
              ) : (
                notifications.slice(0, 10).map((notification) => {
                  const orderIdString = getOrderIdString(notification.orderId);
                  
                  return (
                    <motion.div
                      key={notification._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:shadow-md transition-all ${getNotificationBgColor(
                        notification.type,
                        notification.isRead
                      )} ${!notification.isRead ? 'border-l-4 border-l-emerald-500' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            notification.isRead ? 'bg-gray-200' : 'bg-white'
                          }`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${notification.isRead ? 'text-gray-600' : 'text-gray-900'}`}>
                              {notification.title || getNotificationTitle(notification.type)}
                            </p>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-2 flex-shrink-0">
                              {formatTime(notification.createdAt)}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${notification.isRead ? 'text-gray-500' : 'text-gray-700'}`}>
                            {notification.message}
                          </p>
                          {orderIdString && (
                            <p className="text-xs text-emerald-600 mt-2 font-medium flex items-center gap-1">
                              <span>Click to view order</span>
                              <span className="text-emerald-700">#{getDisplayOrderId(notification)}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Footer - View all link */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-4 py-2 hover:bg-emerald-50 rounded-lg transition-colors"
                >
                  View all notifications ({notifications.length})
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;